import { EConfigurationNames } from '@ConfigLoader/ConfigFiles';
import ConfigLoader from '@ConfigLoader/ConfigLoader';
import { AboveReels } from '@Core/AboveReels/AboveReels';
import SpineWrapper from '@Core/Animation/SpineWrapper';
import { BeforeWinAnimation } from '@Core/BeforeWinAnimation/BeforeWinAnimation';
import { BullshitCollector } from '@Core/Collectors/BullshitCollector';
import { Collector } from '@Core/Collectors/Collector';
import { DynamicCasinoJackpot } from '@Core/DynamicCasinoJackpot/DynamicCasinoJackpot';
import { ExpandingSymbolFeature } from '@Core/ExpandingSymbolFeature/ExpandingSymbolFeature';
import { FeatureDetector } from '@Core/FeaturesDetector';
import { Gamble } from '@Core/Gamble/Gamble';
import { GameBackground } from '@Core/GameBackground/GameBackground';
import { GameComponent } from '@Core/GameComponent/GameComponent';
import { EGameComponentHooks } from '@Core/GameComponent/GameComponentHooks';
import { GameComponentRegistry } from '@Core/GameComponent/GameComponentRegistry';
import { GamePlayMessages } from '@Core/GamePlayMessages/GamePlayMessages';
import { InfoDisplay } from '@Core/InfoDisplay/InfoDisplay';
import AssetsLoadManager from '@Core/Loader/AssetsLoadManager';
import { Reels } from '@Core/Reels/Reels';
import { ESymbolRoles } from '@Core/Reels/SymbolRoles';
import { ReelsBackground } from '@Core/ReelsBackground/ReelsBackground';
import { Scene } from '@Core/Scene/Scene';
import { SceneNames } from '@Core/SceneNames';
import { FreeGames } from '@Core/SpecialGames/FreeGames/FreeGames';
import { Intro } from '@Core/SpecialGames/Intro';
import { LockAndRoll } from '@Core/SpecialGames/LockAndRoll/LockAndRoll';
import { LockAndRollCoinValues } from '@Core/SpecialGames/LockAndRoll/LockAndRollCoinValues';
import { LockAndRollCollection } from '@Core/SpecialGames/LockAndRoll/LockAndRollCollection';
import { LockAndRollJackpotTexts } from '@Core/SpecialGames/LockAndRoll/LockAndRollJackpotTexts';
import { Outro } from '@Core/SpecialGames/Outro';
import { SpecialGames } from '@Core/SpecialGames/SpecialGames';
import { SpecialGamesSpine } from '@Core/SpecialGames/SpecialGamesSpine';
import { StaticJackpotWin } from '@Core/SpecialGames/StaticJackpotWin';
import { StartupLogo } from '@Core/StartupLogo/StartupLogo';
import { StartupPreloader } from '@Core/StartupPreloader/StartupPreloader';
import { StickySymbols } from '@Core/StickySymbols/StickySymbols';
import { ScattersPresenter } from '@Core/SymbolsHighlighter/SymbolsHighlightPresenters/ScattersPresenter';
import { Ways } from '@Core/SymbolsHighlighter/SymbolsHighlightPresenters/Ways';
import { Winlines } from '@Core/SymbolsHighlighter/SymbolsHighlightPresenters/Winlines';
import { SymbolsHighlighter } from '@Core/SymbolsHighlighter/SymbolsHighlighter';
import { Tension } from '@Core/Tension/Tension';
import { UnderWin } from '@Core/UnderWin/UnderWin';
import { WildWheelFeature } from '@Core/WildWheelFeature/WildWheelFeature';
import { BaseWin } from '@Core/Win/BaseWin';
import { BaseWinLevelsConfigParser } from '@Core/Win/BaseWinLevelsConfigParser';
import { BaseWinPresentationSpine } from '@Core/Win/BaseWinPresentationSpine';
import { WinPresentation } from '@Core/Win/WinPresentation';
import { TSpecialSymbolIdsByGameType } from '@GFTypes/GameStoreTypes';
import { ESpecialGamesType } from '@GFTypes/SpecialGames';
import { ISpineConfig } from '@GFTypes/Spines';
import { Game } from '@Game/Game';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { BonusBuy } from '@Managers/BonusBuy/BonusBuy';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { Logger, LoggerLevel, LoggerSource } from '@Managers/Logger';
import { EGameStates } from '@Managers/StateManager/GameStates';
import { StateConditions } from '@Managers/StateManager/StateConditions';
import { TrackEntry } from '@esotericsoftware/spine-pixi-v8';
import { Container, Rectangle } from 'pixi.js';

export class GameScene extends Scene {
    public startupLogo: StartupLogo;
    public startupPreloader: StartupPreloader;
    public gameBackground: GameBackground;
    public reelsBackground: ReelsBackground;
    public reels: Reels;
    public aboveReels: AboveReels;
    public underWin: UnderWin;
    public tension: Tension;
    public winPresentation: WinPresentation;
    public ways: Ways;
    public winlines: Winlines;
    public gamble: Gamble;
    public gamePlayMessages: GamePlayMessages;
    public infoDisplay: InfoDisplay;
    public features: GameComponent[];
    public specialGames: SpecialGames;
    public specialGamesSpine: SpineWrapper;
    public freeGames: FreeGames;
    public lockAndRoll: LockAndRoll;
    public lockAndRollCoinValues: LockAndRollCoinValues;
    public lockAndRollJackpotTexts: LockAndRollJackpotTexts;
    public lockAndRollCollection: LockAndRollCollection;
    public symbolHighlighter: SymbolsHighlighter;
    public stickySymbols: StickySymbols;
    public baseWinPresentation: BaseWin;
    public baseWinPresentationSpine: SpineWrapper;
    public bullshitCollector: BullshitCollector;
    public collector: Collector;
    public bonusBuy: BonusBuy;
    public dynamicCasinoJackpot: DynamicCasinoJackpot;

    private isRunning: boolean = false;
    private readonly spinesWithCustomSlots: SpineWrapper[] = [];
    private expandingWildFeature: ExpandingSymbolFeature;
    private intro: Intro;
    private outro: Outro;

    constructor(game: Game) {
        super(game);
        Logger.log(LoggerLevel.Constructor, {
            class: SceneNames.GAME_SCENE,
            message: 'GameScene constructor',
            source: LoggerSource.Game
        });
    }

    public get screen(): Rectangle {
        return this.game.renderer.screen;
    }

    public get stage(): Container {
        return this.game.renderer.stage;
    }

    public preload(): void {}

    public init(): void {
        super.init();
        this.preload();
        this.startupLogo = new StartupLogo(this);
        this.startupLogo.init();
        this.startupPreloader = new StartupPreloader(this);
        this.startupPreloader.init();
        EventsCenter.on(EGameEvents.PRELOADER_INFO_CONTINUE, () => this.continueAction());
        this.game.scaleManager.onResize(this);
    }

    private continueAction(): void {
        this.initilizeComponents();
        this.game.inputManager.initializeInputs();
        GameComponentRegistry.callHook(this, EGameComponentHooks.GameStoreInit);
        GameComponentRegistry.callHook(this, EGameComponentHooks.Init);
        this.create();

        this.game.gameStore.set(EGameStoreKeys.PreloaderState, { active: false }, true);
    }

    private initilizeComponents(): void {
        const authConfig = this.gameStore.get(EGameStoreKeys.AuthenticateGameConfig);
        const declaredAssets = AssetsLoadManager.getSpinesConfiguration().assets;
        const detectedFeatures = FeatureDetector.getFeatures(this, this.gameStore, authConfig.symbols, declaredAssets);
        const {
            aboveReels,
            tension,
            gamble,
            underWin,
            infoDisplay,
            wildWheel,
            specialGames,
            expandingSymbol,
            winlines,
            scatter,
            staticJackpot,
            collector,
            bullshitCollector,
            dynamicCasinoJackpot,
            bonusBuy,
            winMultiplierPresentation,
            multiSymbol
        } = detectedFeatures;
        this.gameStore.set(EGameStoreKeys.Features, detectedFeatures);
        this.baseWinPresentationSpine = new BaseWinPresentationSpine(this).spineWrapper;
        this.gameBackground = new GameBackground(this);
        this.reelsBackground = new ReelsBackground(this, detectedFeatures.maxWin);
        this.reels = new Reels(this, multiSymbol);
        if (aboveReels) this.aboveReels = new AboveReels(this);
        if (tension) this.tension = new Tension(this);
        if (gamble) this.gamble = new Gamble(this);
        if (underWin) this.underWin = new UnderWin(this);
        const { winLevels, win, lockAndRollAssets: lockAndRollAssetsConfig } = ConfigLoader.getConfiguration(EConfigurationNames.GAME);
        this.winPresentation = new WinPresentation(this, this.baseWinPresentationSpine);

        const baseWinLevelsConfigParser = new BaseWinLevelsConfigParser(
            winLevels,
            win,
            this.game.soundManager.winSoundsManager,
            this,
            this.baseWinPresentationSpine
        );
        this.winPresentation.setDefaultParser(baseWinLevelsConfigParser);

        if (staticJackpot) new StaticJackpotWin(this, this.winPresentation, staticJackpot);
        this.gamePlayMessages = new GamePlayMessages(this);
        if (infoDisplay) this.infoDisplay = new InfoDisplay(this);
        //this.wintab = new Wintab(this);
        const mainPresenter = winlines ? new Winlines(this) : new Ways(this);
        const scattersPresenter = scatter ? new ScattersPresenter(this, scatter) : undefined;
        this.baseWinPresentation = new BaseWin(this, this.winPresentation, this.baseWinPresentationSpine, winMultiplierPresentation);
        this.symbolHighlighter = new SymbolsHighlighter(this, mainPresenter, scattersPresenter);
        // TODO: Make it based on config
        this.features = [];
        if (wildWheel) this.features.push(new WildWheelFeature(this));

        this.stickySymbols = new StickySymbols(this);
        if (specialGames) {
            this.specialGamesSpine = new SpecialGamesSpine(this).spineWrapper;
            let specialSymbolsMap: TSpecialSymbolIdsByGameType | undefined = specialGames[ESpecialGamesType.FREE_GAMES]?.[ESymbolRoles.SPECIAL_SYMBOL];
            let specialSymbolSpine: SpineWrapper | undefined = specialSymbolsMap ? this.specialGamesSpine : undefined;
            this.specialGames = new SpecialGames(this);

            this.intro = new Intro(this, this.specialGamesSpine, this.specialGamesSpine, specialSymbolSpine, specialSymbolsMap);
            this.outro = new Outro(this, this.specialGamesSpine, this.specialGamesSpine);

            if (specialGames[ESpecialGamesType.FREE_GAMES]) this.freeGames = new FreeGames(this);

            if (specialGames[ESpecialGamesType.LOCK_AND_ROLL]) {
                this.lockAndRoll = new LockAndRoll(this);
                this.lockAndRollCoinValues = new LockAndRollCoinValues(this);
                this.lockAndRollJackpotTexts = new LockAndRollJackpotTexts(this);
                this.lockAndRollCollection = new LockAndRollCollection(this, lockAndRollAssetsConfig);
            }
        }
        if (expandingSymbol) this.expandingWildFeature = new ExpandingSymbolFeature(this, expandingSymbol, this.reels);
        if (bullshitCollector) this.bullshitCollector = new BullshitCollector(this);
        if (collector) this.collector = new Collector(this);
        if (bonusBuy) this.bonusBuy = new BonusBuy(this, this.game);

        if (dynamicCasinoJackpot) this.dynamicCasinoJackpot = new DynamicCasinoJackpot(this, dynamicCasinoJackpot);

        this.features.push(new BeforeWinAnimation(this));
    }

    public create(): void {
        this.showGame();
        GameComponentRegistry.callHook(this, EGameComponentHooks.Create);
        this.waitForStartupAnimations();
        this.createCustomSpines();
        this.game.initiateStateManager();
    }

    private async waitForStartupAnimations(): Promise<void> {
        const promises: Promise<TrackEntry | void>[] = [];
        this.game.spines.forEach((spine) => {
            if (spine.visible && spine.spine.visible && spine.eventManager.hasGameEventAnimation(EGameStates.GAME_START)) {
                promises.push(spine.waitForAnimationEvent(EGameStates.GAME_START, 'complete').promise);
            }
        });

        await Promise.all(promises);

        StateConditions.gameStartCondition.resolve();
    }

    public update(delta: number): void {
        this.game.scaleManager.onUpdate(this, delta);
        this.reels?.update(delta);
        this.winPresentation?.update(delta);
    }

    public addSpineWithCustomSlot(spine: SpineWrapper): void {
        this.spinesWithCustomSlots.push(spine);
    }

    public removeSpineWithCustomSlot(spine: SpineWrapper): void {
        const index = this.spinesWithCustomSlots.indexOf(spine);

        if (index > -1) {
            this.spinesWithCustomSlots.splice(index, 1);
        }
    }

    private showGame(): void {
        if (this.isRunning) return;
        this.isRunning = true;
    }

    private createCustomSpines(): void {
        AssetsLoadManager.getSpinesConfiguration()
            .assets.filter((spine) => spine.custom)
            .forEach((spineConfig) => {
                const animationConfig: ISpineConfig = {
                    dataKey: spineConfig.key,
                    skinName: 'default'
                };

                const centeredPosition = {
                    x: this.screen.width / 2,
                    y: this.screen.height / 2
                };

                new SpineWrapper(this, centeredPosition, animationConfig, true);
            });
    }
}
